import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation as useLocationContext } from '../contexts/LocationContext';
import { 
  ArrowLeft,
  Star,
  MapPin,
  Calendar,
  Users,
  Send,
  Search,
  ChevronRight,
  Home,
  Bookmark,
  User,
  Loader2,
  Plus,
  MessageCircle,
  Heart,
  Filter,
  Globe,
  Compass,
  UserPlus,
  Shield,
  Check,
  X,
  Clock,
  Sparkles,
  TrendingUp,
  Eye,
  EyeOff,
  Settings,
  Mail,
  AlertCircle,
  Crown,
  Lock,
  Megaphone,
  HandHeart,
  MessageSquare,
  Phone,
  AtSign,
  Flag,
  Ban,
  ChevronDown,
  Zap,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import FloatingSupport from '../components/FloatingSupport';
import TripChat from '../components/TripChat';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

// Join request message templates
const JOIN_TEMPLATES = [
  { id: 'plan_together', label: "I'd love to plan this trip together!", icon: Calendar },
  { id: 'explore_together', label: 'Would be great to explore together.', icon: Compass },
  { id: 'share_experience', label: 'Looking forward to sharing this experience!', icon: Heart },
  { id: 'similar_interests', label: 'We have similar interests. Let\'s connect!', icon: Sparkles },
];

// Travel style options
const TRAVEL_STYLES = [
  'adventure', 'relaxed', 'cultural', 'luxury', 'budget', 'photography', 'foodie', 'nature'
];

// Interest options
const INTEREST_OPTIONS = [
  'photography', 'hiking', 'food', 'culture', 'history', 'nightlife', 
  'beaches', 'mountains', 'temples', 'wildlife', 'adventure sports', 'local markets'
];

// Destination images
const DESTINATION_IMAGES = {
  'Ladakh': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=80',
  'Leh-Ladakh': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=80',
  'Goa': 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=400&q=80',
  'South Goa': 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=400&q=80',
  'Bali': 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400&q=80',
  'Rajasthan': 'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=400&q=80',
  'Kerala': 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=400&q=80',
  'Japan': 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=400&q=80',
  'Mumbai': 'https://images.unsplash.com/photo-1566552881560-0be862a7c445?w=400&q=80',
  'Mathura': 'https://images.unsplash.com/photo-1548013146-72479768bada?w=400&q=80',
  'default': 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&q=80'
};

export default function Community() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { countryCode } = useLocationContext();
  
  // Tab state
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(urlTab || 'trips');
  
  // Data states
  const [communityStats, setCommunityStats] = useState({ total_members: '3,665+', active_posts: 0 });
  const [posts, setPosts] = useState([]);
  const [myMatches, setMyMatches] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  
  // UI states
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [createPostType, setCreatePostType] = useState('travel_invite');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showProModal, setShowProModal] = useState(false);
  
  // Trip Chat state
  const [activeChatMatch, setActiveChatMatch] = useState(null);
  
  // Pro status
  const [isPro, setIsPro] = useState(false);
  
  // Create post form
  const [newPost, setNewPost] = useState({
    destination: '',
    time_window: '',
    travel_style: 'relaxed',
    group_size_preference: '2-3',
    description: '',
    interests: [],
    budget_range: 'mid-range',
    title: '',  // For broadcast
    event_name: '',
    event_date: ''
  });
  
  // Load data
  useEffect(() => {
    loadData();
  }, []);
  
  // Load events when events tab is selected
  useEffect(() => {
    if (activeTab === 'events' && events.length === 0) {
      loadEvents();
    }
  }, [activeTab]);
  
  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      // Try AI-powered trending events first (Gemini-based)
      const aiEvents = await api.getAITrendingEvents(8);
      if (aiEvents.success && aiEvents.events?.length > 0) {
        const formattedEvents = aiEvents.events.map((e, idx) => ({
          id: e.id || `ai_${idx}`,
          name: e.name,
          city: e.city,
          country: e.country || 'International',
          date: e.date,
          category: e.category || 'festival',
          image: e.image || 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=400&q=80',
          venue: e.venue || e.city,
          description: e.description || `${e.name} in ${e.city}`,
          ticket_url: e.ticket_url,
          is_featured: e.is_featured,
          expected_attendees: e.expected_attendees,
          price_range: e.price_range,
          tags: e.tags || [],
          source: 'ai_powered'
        }));
        setEvents(formattedEvents);
      } else {
        // Fallback to prioritized events
        const prioritizedEvents = await api.getPrioritizedEvents(countryCode);
        if (prioritizedEvents.success && prioritizedEvents.events?.length > 0) {
          const formattedEvents = prioritizedEvents.events.map((e, idx) => ({
            id: e.id || idx,
            name: e.name,
            city: e.city,
            country: e.country || 'India',
            date: e.date,
            category: e.category || 'festival',
            image: e.image || 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=400&q=80',
            venue: e.venue || e.city,
            description: e.description || `${e.name} in ${e.city}`,
            ticket_url: e.ticket_url,
            is_featured: e.is_featured,
            is_local: e.country === (countryCode === 'IN' ? 'India' : countryCode === 'US' ? 'USA' : countryCode)
          }));
          setEvents(formattedEvents);
        } else {
          setEvents(getCuratedEvents());
        }
      }
    } catch (error) {
      console.error('Failed to load dynamic events:', error);
      // Try prioritized events as fallback
      try {
        const prioritizedEvents = await api.getPrioritizedEvents(countryCode);
        if (prioritizedEvents.success && prioritizedEvents.events?.length > 0) {
          const formattedEvents = prioritizedEvents.events.map((e, idx) => ({
            id: e.id || idx,
            name: e.name,
            city: e.city,
            country: e.country || 'India',
            date: e.date,
            category: e.category || 'festival',
            image: e.image || 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=400&q=80',
            venue: e.venue || e.city,
            description: e.description || `${e.name} in ${e.city}`,
            ticket_url: e.ticket_url,
            is_featured: e.is_featured,
            is_local: e.country === (countryCode === 'IN' ? 'India' : countryCode === 'US' ? 'USA' : countryCode)
          }));
          setEvents(formattedEvents);
        } else {
          setEvents(getCuratedEvents());
        }
      } catch {
        setEvents(getCuratedEvents());
      }
    }
    setEventsLoading(false);
  };
  
  const getCuratedEvents = () => [
    { id: 1, name: 'Coldplay Music of the Spheres', city: 'Mumbai', country: 'India', date: '2026-01-25', category: 'concert', image: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400&q=80', venue: 'DY Patil Stadium', description: 'World tour finale in India - One of the biggest concerts of the year!' },
    { id: 2, name: 'India Art Fair 2026', city: 'New Delhi', country: 'India', date: '2026-02-06', category: 'cultural', image: 'https://images.unsplash.com/photo-1536924940846-227afb31e2a5?w=400&q=80', venue: 'NSIC Grounds', description: 'South Asias largest art fair featuring 80+ galleries' },
    { id: 3, name: 'Holi Festival', city: 'Mathura', country: 'India', date: '2026-03-14', category: 'festival', image: 'https://images.unsplash.com/photo-1576444356170-66073046b1bc?w=400&q=80', venue: 'Banke Bihari Temple', description: 'Experience the most vibrant Holi celebrations in India' },
    { id: 4, name: 'Goa Carnival 2026', city: 'Goa', country: 'India', date: '2026-02-14', category: 'festival', image: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=400&q=80', venue: 'Panaji Streets', description: 'Four days of parades, music, and festivities' },
    { id: 5, name: 'Sunburn Festival', city: 'Goa', country: 'India', date: '2026-12-28', category: 'concert', image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=400&q=80', venue: 'Vagator Beach', description: 'Asias largest EDM festival with world-class DJs' },
  ];
  
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load community stats
      const statsRes = await api.getCommunityStats().catch(() => ({ stats: communityStats }));
      if (statsRes?.stats) {
        setCommunityStats(statsRes.stats);
      }
      
      // Load posts
      const postsRes = await api.getCommunityPosts().catch(() => ({ posts: getDemoPosts() }));
      setPosts(postsRes?.posts?.length > 0 ? postsRes.posts : getDemoPosts());
      
      // Load user's matches if authenticated
      if (isAuthenticated && user?.user_id) {
        const matchesRes = await api.getMyTripMatches(user.user_id).catch(() => ({ matches: [] }));
        setMyMatches(matchesRes?.matches || []);
        
        // Check Pro status
        const proRes = await api.getProStatus(user.user_id).catch(() => ({ is_pro: false }));
        setIsPro(proRes?.is_pro || false);
      }
    } catch (error) {
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Genuine-looking demo posts with future dates
  const getDemoPosts = () => {
    const now = new Date();
    const getMonthYear = (monthsAhead) => {
      const future = new Date(now);
      future.setMonth(future.getMonth() + monthsAhead);
      return future.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };
    
    return [
      // Trip Invites
      {
        post_id: 'inv_leh_001',
        destination: 'Leh-Ladakh',
        time_window: getMonthYear(5),
        travel_style: 'adventure',
        group_size_preference: '2-3',
        description: 'Planning the classic Manali-Leh highway trip! Want to explore Pangong Lake, Nubra Valley, and Khardung La. Looking for fellow adventurers who love photography and can handle high altitude. Will be renting Royal Enfields.',
        interests: ['photography', 'biking', 'mountains'],
        budget_range: 'mid-range',
        post_type: 'travel_invite',
        status: 'active',
        is_anonymous: true,
        created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(Date.now() + 150 * 24 * 60 * 60 * 1000).toISOString(),
        view_count: 234,
        interested_count: 18
      },
      {
        post_id: 'inv_goa_002',
        destination: 'South Goa',
        time_window: getMonthYear(2),
        travel_style: 'relaxed',
        group_size_preference: '3-4',
        description: 'Escaping to Palolem and Agonda beaches for a week. Looking for chill people who enjoy morning yoga, beach cafes, and sunset sessions. No party scene - just peace, good food, and ocean views.',
        interests: ['beach', 'yoga', 'food'],
        budget_range: 'mid-range',
        post_type: 'travel_invite',
        status: 'active',
        is_anonymous: true,
        created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
        view_count: 187,
        interested_count: 24
      },
      {
        post_id: 'inv_japan_003',
        destination: 'Japan',
        time_window: getMonthYear(3),
        travel_style: 'cultural',
        group_size_preference: '2-3',
        description: 'Cherry blossom season itinerary: Tokyo → Hakone → Kyoto → Osaka. 12-day trip covering temples, ryokans, street food, and of course, sakura! Looking for travel buddies interested in Japanese culture and photography.',
        interests: ['photography', 'culture', 'food'],
        budget_range: 'luxury',
        post_type: 'travel_invite',
        status: 'active',
        is_anonymous: true,
        created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
        view_count: 312,
        interested_count: 31
      },
      {
        post_id: 'inv_kerala_004',
        destination: 'Kerala',
        time_window: getMonthYear(1),
        travel_style: 'relaxed',
        group_size_preference: '2-3',
        description: 'Backwaters and beyond! Planning Alleppey houseboat → Munnar tea gardens → Thekkady wildlife. Looking for someone who appreciates slow travel, authentic Kerala cuisine, and doesn\'t mind early morning bird watching.',
        interests: ['nature', 'food', 'wildlife'],
        budget_range: 'mid-range',
        post_type: 'travel_invite',
        status: 'active',
        is_anonymous: true,
        created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString(),
        view_count: 145,
        interested_count: 16
      },
      {
        post_id: 'inv_bali_005',
        destination: 'Bali',
        time_window: getMonthYear(4),
        travel_style: 'adventure',
        group_size_preference: '3-4',
        description: 'Ultimate Bali adventure: Ubud rice terraces, Mount Batur sunrise trek, Nusa Penida day trip, and Seminyak beaches. 10 days of exploration. Looking for energetic travel companions who love hiking and water activities!',
        interests: ['hiking', 'beaches', 'adventure'],
        budget_range: 'mid-range',
        post_type: 'travel_invite',
        status: 'active',
        is_anonymous: true,
        created_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(Date.now() + 120 * 24 * 60 * 60 * 1000).toISOString(),
        view_count: 278,
        interested_count: 22
      },
      {
        post_id: 'inv_rajasthan_006',
        destination: 'Rajasthan',
        time_window: getMonthYear(2),
        travel_style: 'cultural',
        group_size_preference: '2-3',
        description: 'Royal Rajasthan circuit: Jaipur → Jodhpur → Jaisalmer → Udaipur. Want to experience heritage hotels, desert camping, and local food trails. Photography enthusiasts preferred - golden hour at forts is the goal!',
        interests: ['photography', 'history', 'culture'],
        budget_range: 'luxury',
        post_type: 'travel_invite',
        status: 'active',
        is_anonymous: true,
        created_at: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(Date.now() + 75 * 24 * 60 * 60 * 1000).toISOString(),
        view_count: 198,
        interested_count: 14
      },
      // Broadcasts
      {
        post_id: 'bcast_ipl_001',
        title: 'IPL Finals Watch Party in Mumbai?',
        description: 'Anyone planning to watch IPL finals live at Wankhede or at a fan zone? Would be amazing to experience the atmosphere with fellow cricket enthusiasts. Let\'s coordinate!',
        destination: 'Mumbai',
        event_name: 'IPL 2026 Finals',
        event_date: getMonthYear(4),
        post_type: 'broadcast',
        status: 'active',
        is_anonymous: true,
        created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString(),
        view_count: 456,
        interested_count: 67
      },
      {
        post_id: 'bcast_hemis_002',
        title: 'Hemis Festival Trip - Group forming!',
        description: 'The famous Hemis Festival in Ladakh features incredible mask dances and Buddhist ceremonies. Planning to attend this year - anyone interested in making it a group trip? We can share logistics and costs.',
        destination: 'Ladakh',
        event_name: 'Hemis Festival',
        event_date: getMonthYear(6),
        post_type: 'broadcast',
        status: 'active',
        is_anonymous: true,
        created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        view_count: 189,
        interested_count: 28
      },
      {
        post_id: 'bcast_holi_003',
        title: 'Holi in Mathura-Vrindavan anyone?',
        description: 'Experiencing Holi in Braj region is on my bucket list! The famous Lathmar Holi and flower Holi are surreal. Looking to connect with others planning the same. We can book guesthouses together.',
        destination: 'Mathura',
        event_name: 'Holi Festival',
        event_date: getMonthYear(2),
        post_type: 'broadcast',
        status: 'active',
        is_anonymous: true,
        created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(Date.now() + 11 * 24 * 60 * 60 * 1000).toISOString(),
        view_count: 234,
        interested_count: 41
      },
      {
        post_id: 'bcast_sunburn_004',
        title: 'Sunburn Goa 2026 - Splitting accommodation?',
        description: 'Sunburn festival tickets booked! Looking for people to share villa/accommodation costs in Vagator area. Prefer people who are into EDM but also want to explore Goa beyond the festival.',
        destination: 'Goa',
        event_name: 'Sunburn Festival',
        event_date: getMonthYear(11),
        post_type: 'broadcast',
        status: 'active',
        is_anonymous: true,
        created_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
        view_count: 345,
        interested_count: 52
      }
    ];
  };
  
  // Filter posts
  const filteredPosts = posts.filter(post => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      post.destination?.toLowerCase().includes(query) ||
      post.description?.toLowerCase().includes(query) ||
      post.title?.toLowerCase().includes(query) ||
      post.event_name?.toLowerCase().includes(query)
    );
  });
  
  // Create post handler
  const handleCreatePost = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to create a post');
      navigate('/auth');
      return;
    }
    
    if (createPostType === 'travel_invite') {
      if (!newPost.destination || !newPost.time_window || !newPost.description) {
        toast.error('Please fill in all required fields');
        return;
      }
    } else {
      if (!newPost.title || !newPost.description) {
        toast.error('Please fill in title and description');
        return;
      }
    }
    
    try {
      let result;
      if (createPostType === 'travel_invite') {
        result = await api.createTravelInvite({
          destination: newPost.destination,
          time_window: newPost.time_window,
          travel_style: newPost.travel_style,
          group_size_preference: newPost.group_size_preference,
          description: newPost.description,
          interests: newPost.interests,
          budget_range: newPost.budget_range
        }, user?.user_id);
      } else {
        result = await api.createBroadcast({
          title: newPost.title,
          description: newPost.description,
          destination: newPost.destination,
          event_name: newPost.event_name,
          event_date: newPost.event_date
        }, user?.user_id);
      }
      
      if (result?.success) {
        toast.success('Post created successfully!');
        setShowCreatePost(false);
        setNewPost({
          destination: '', time_window: '', travel_style: 'relaxed',
          group_size_preference: '2-3', description: '', interests: [],
          budget_range: 'mid-range', title: '', event_name: '', event_date: ''
        });
        loadData();
      } else {
        toast.error(result?.error || 'Failed to create post');
      }
    } catch (error) {
      toast.error('Failed to create post');
    }
  };
  
  // Send join request
  const handleSendJoinRequest = async (postId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to send requests');
      navigate('/auth');
      return;
    }
    
    if (!selectedTemplate) {
      toast.error('Please select a message');
      return;
    }
    
    try {
      const result = await api.sendJoinRequest({
        post_id: postId,
        message_template: selectedTemplate
      }, user?.user_id);
      
      if (result?.success) {
        toast.success('Join request sent!');
        setSelectedPost(null);
        setSelectedTemplate(null);
      } else {
        toast.error(result?.error || 'Failed to send request');
      }
    } catch (error) {
      toast.error('Failed to send request');
    }
  };
  
  // Toggle interest selection
  const toggleInterest = (interest) => {
    setNewPost(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests.slice(0, 4), interest]  // Max 5 interests
    }));
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
            data-testid="home-btn"
          >
            <Home size={20} />
          </Link>
          
          <div className="flex items-center gap-2">
            <PanchiBirdLogo className="w-9 h-9" />
            <span className="font-bold text-xl text-gray-900">Community</span>
          </div>
          
          <button 
            onClick={() => setShowCreatePost(true)}
            className="p-2.5 text-white bg-gradient-to-r from-teal-500 to-cyan-500 shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 transition-all"
            style={{ borderRadius: '50%' }}
            data-testid="create-post-btn"
          >
            <Plus size={20} />
          </button>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6 overflow-x-hidden">
        {/* Community Stats Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg"
          style={{ borderRadius: '1rem' }}
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0" style={{ borderRadius: '0.75rem' }}>
                <Users size={20} className="sm:w-6 sm:h-6" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-black">{communityStats.total_members}</h2>
                <p className="text-white/80 text-xs sm:text-sm">travellers in community</p>
              </div>
            </div>
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-bold">{communityStats.active_posts || filteredPosts.length}</p>
                <p className="text-white/80 text-xs">Active Trips</p>
              </div>
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-bold">{communityStats.active_trip_matches || 8}</p>
                <p className="text-white/80 text-xs">Trip Matches</p>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Tabs - Scrollable on mobile */}
        <div className="flex items-center gap-2 p-1.5 bg-gray-100/80 backdrop-blur mb-6 overflow-x-auto scrollbar-hide" style={{ borderRadius: '9999px' }}>
          {[
            { id: 'events', label: 'Events', icon: Star },
            { id: 'trips', label: 'Trip Invites', icon: MapPin },
            { id: 'broadcasts', label: 'Broadcasts', icon: Megaphone },
            { id: 'matches', label: 'My Matches', icon: HandHeart, badge: myMatches.length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 font-semibold transition-all whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
                activeTab === tab.id
                  ? 'bg-white text-teal-600 shadow-md'
                  : 'text-gray-600 hover:text-teal-600'
              }`}
              style={{ borderRadius: '9999px' }}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} />
              {tab.label}
              {tab.badge > 0 && activeTab !== tab.id && (
                <span className="w-5 h-5 bg-teal-500 text-white text-xs font-bold flex items-center justify-center" style={{ borderRadius: '50%' }}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Search */}
        <div className="relative mb-6">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by destination, event, or keyword..."
            className="w-full pl-12 pr-4 py-3.5 bg-white border-2 border-gray-100 text-gray-900 placeholder-gray-400 focus:border-teal-400 outline-none transition-all shadow-sm"
            style={{ borderRadius: '9999px' }}
            data-testid="search-input"
          />
        </div>
        
        {/* Privacy Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-100"
          style={{ borderRadius: '1rem' }}
        >
          <div className="flex items-start gap-3">
            <Shield className="text-teal-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-teal-800 text-sm">
                <strong>Privacy First:</strong> All posts are anonymous by default. Your identity is only revealed 
                after mutual acceptance of trip coordination. Communication unlocks only for Pro members within 
                matched trips.
              </p>
            </div>
          </div>
        </motion.div>
        
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 size={40} className="animate-spin text-teal-500 mb-4" />
            <p className="text-gray-500 font-medium">Loading community...</p>
          </div>
        ) : (
          <>
            {/* Events Tab */}
            {activeTab === 'events' && (
              <div className="space-y-6">
                {/* Events Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Popular Events</h2>
                    <p className="text-gray-500">Discover must-attend events and find travel companions</p>
                  </div>
                </div>
                
                {eventsLoading ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Loader2 size={40} className="animate-spin text-teal-500 mb-4" />
                    <p className="text-gray-500">Loading events...</p>
                  </div>
                ) : events.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-24 h-24 bg-amber-100 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '2rem' }}>
                      <Star size={48} className="text-amber-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">No events found</h3>
                    <p className="text-gray-500 mb-6">Check back later for upcoming events!</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {events.map((event, index) => (
                      <motion.div
                        key={event.id || index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="group bg-white border border-gray-100 shadow-md hover:shadow-xl transition-all overflow-hidden cursor-pointer"
                        style={{ borderRadius: '1.25rem' }}
                        onClick={() => navigate(`/planning?destination=${encodeURIComponent(event.city)}&event=${encodeURIComponent(event.name)}`)}
                        data-testid={`event-card-${index}`}
                      >
                        {/* Event Image */}
                        <div className="relative h-40 overflow-hidden">
                          <img 
                            src={event.image || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&q=80'} 
                            alt={event.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={(e) => {
                              // Fallback images based on category
                              const fallbacks = {
                                sports: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=600&q=80',
                                festival: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=600&q=80',
                                music: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&q=80',
                                concert: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&q=80',
                                cultural: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=600&q=80',
                                food: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=600&q=80',
                              };
                              e.target.src = fallbacks[event.category] || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&q=80';
                              e.target.onerror = null; // Prevent infinite loop
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          
                          {/* Category Badge */}
                          <span className={`absolute top-3 left-3 px-3 py-1 text-xs font-bold text-white ${
                            event.category === 'concert' ? 'bg-purple-500' :
                            event.category === 'festival' ? 'bg-rose-500' :
                            event.category === 'sports' ? 'bg-green-500' :
                            event.category === 'cultural' ? 'bg-amber-500' :
                            'bg-teal-500'
                          }`} style={{ borderRadius: '9999px' }}>
                            {event.category?.charAt(0).toUpperCase() + event.category?.slice(1) || 'Event'}
                          </span>
                          
                          {/* Date Badge */}
                          <div className="absolute bottom-3 left-3 text-white">
                            <p className="text-lg font-bold">{event.name}</p>
                          </div>
                        </div>
                        
                        {/* Event Details */}
                        <div className="p-4">
                          <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
                            <MapPin size={14} className="text-teal-500" />
                            <span>{event.city}{event.country && event.country !== 'India' ? `, ${event.country}` : ''}</span>
                            {event.venue && event.venue !== event.city && (
                              <>
                                <span className="text-gray-300">•</span>
                                <span className="truncate">{event.venue}</span>
                              </>
                            )}
                          </div>
                          
                          {event.is_featured && (
                            <div className="mb-2">
                              <span className="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold" style={{ borderRadius: '9999px' }}>
                                ⭐ Featured Event
                              </span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 text-gray-600 text-sm mb-3">
                            <Calendar size={14} className="text-amber-500" />
                            <span>{new Date(event.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                          
                          {event.description && (
                            <p className="text-gray-500 text-sm line-clamp-2 mb-3">{event.description}</p>
                          )}
                          
                          {/* Price and Attendees info for AI events */}
                          {(event.price_range || event.expected_attendees) && (
                            <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                              {event.price_range && (
                                <span className="flex items-center gap-1">
                                  <DollarSign size={12} />
                                  {event.price_range}
                                </span>
                              )}
                              {event.expected_attendees && (
                                <span className="flex items-center gap-1">
                                  <Users size={12} />
                                  {event.expected_attendees.toLocaleString()}+ expected
                                </span>
                              )}
                            </div>
                          )}
                          
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/planning?destination=${encodeURIComponent(event.city)}&event=${encodeURIComponent(event.name)}`);
                            }}
                            className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold text-sm hover:shadow-lg hover:from-teal-600 hover:to-cyan-600 transition-all flex items-center justify-center gap-2"
                            style={{ borderRadius: '9999px' }}
                            data-testid={`plan-trip-event-btn-${index}`}
                          >
                            <Compass size={16} />
                            Plan Trip Around This Event
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
                
                {/* Find Travel Companions CTA */}
                <div className="mt-8 p-6 bg-gradient-to-r from-amber-50 to-rose-50 border border-amber-200" style={{ borderRadius: '1.25rem' }}>
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="w-16 h-16 bg-amber-100 flex items-center justify-center flex-shrink-0" style={{ borderRadius: '1rem' }}>
                      <Users size={32} className="text-amber-600" />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Want to find travel companions for an event?</h3>
                      <p className="text-gray-600 text-sm">Create a Trip Invite or Broadcast to connect with others going to the same event!</p>
                    </div>
                    <button
                      onClick={() => { setActiveTab('trips'); setShowCreatePost(true); }}
                      className="px-6 py-3 bg-amber-500 text-white font-bold hover:bg-amber-600 transition-colors flex items-center gap-2"
                      style={{ borderRadius: '9999px' }}
                    >
                      <Plus size={18} />
                      Create Post
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Trip Invites Tab */}
            {activeTab === 'trips' && (
              <div className="space-y-4">
                {filteredPosts.filter(p => p.post_type === 'travel_invite').length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-24 h-24 bg-teal-100 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '2rem' }}>
                      <MapPin size={48} className="text-teal-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">No trip invites yet</h3>
                    <p className="text-gray-500 mb-6 max-w-md mx-auto">Be the first to post a travel invite and find your travel buddies!</p>
                    <button 
                      onClick={() => { setCreatePostType('travel_invite'); setShowCreatePost(true); }}
                      className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 transition-all"
                      style={{ borderRadius: '9999px' }}
                    >
                      <Plus size={20} />
                      Create Travel Invite
                    </button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {filteredPosts.filter(p => p.post_type === 'travel_invite').map((post, index) => (
                      <motion.div
                        key={post.post_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white border border-gray-100 shadow-md hover:shadow-xl transition-all overflow-hidden"
                        style={{ borderRadius: '1.5rem' }}
                      >
                        {/* Destination Header */}
                        <div className="relative h-32 overflow-hidden">
                          <img 
                            src={DESTINATION_IMAGES[post.destination] || DESTINATION_IMAGES.default}
                            alt={post.destination}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                          <div className="absolute bottom-3 left-4 right-4">
                            <h3 className="text-white font-bold text-xl">{post.destination}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Calendar size={14} className="text-white/80" />
                              <span className="text-white/90 text-sm">{post.time_window}</span>
                            </div>
                          </div>
                          <span className="absolute top-3 right-3 px-3 py-1 bg-white/90 backdrop-blur text-xs font-bold text-gray-700 capitalize" style={{ borderRadius: '9999px' }}>
                            {post.travel_style}
                          </span>
                        </div>
                        
                        {/* Content */}
                        <div className="p-5">
                          {/* Anonymous Traveler Info */}
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center" style={{ borderRadius: '50%' }}>
                              <User size={20} className="text-white" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Anonymous Traveler</p>
                              <p className="text-xs text-gray-500">Looking for {post.group_size_preference} companions</p>
                            </div>
                          </div>
                          
                          {/* Description */}
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3">{post.description}</p>
                          
                          {/* Interests */}
                          {post.interests?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-4">
                              {post.interests.map(interest => (
                                <span key={interest} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs capitalize" style={{ borderRadius: '9999px' }}>
                                  {interest}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {/* Stats & Action */}
                          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-4 text-sm text-gray-400">
                              <span className="flex items-center gap-1">
                                <Eye size={14} />
                                {post.view_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <Heart size={14} />
                                {post.interested_count}
                              </span>
                            </div>
                            <button
                              onClick={() => {
                                if (!isAuthenticated) {
                                  toast.error('Please sign in first');
                                  navigate('/auth');
                                  return;
                                }
                                if (!isPro) {
                                  setShowProModal(true);
                                  return;
                                }
                                setSelectedPost(post);
                              }}
                              className="px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-1.5"
                              style={{ borderRadius: '9999px' }}
                              data-testid={`interested-btn-${post.post_id}`}
                            >
                              {!isPro && <Lock size={14} />}
                              I'm Interested
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Broadcasts Tab */}
            {activeTab === 'broadcasts' && (
              <div className="space-y-4">
                {filteredPosts.filter(p => p.post_type === 'broadcast').length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-24 h-24 bg-amber-100 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '2rem' }}>
                      <Megaphone size={48} className="text-amber-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">No broadcasts yet</h3>
                    <p className="text-gray-500 mb-6 max-w-md mx-auto">Post about festivals, events, or seasonal travel to find interested travelers!</p>
                    <button 
                      onClick={() => { setCreatePostType('broadcast'); setShowCreatePost(true); }}
                      className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-lg hover:shadow-xl transition-all"
                      style={{ borderRadius: '9999px' }}
                    >
                      <Plus size={20} />
                      Create Broadcast
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredPosts.filter(p => p.post_type === 'broadcast').map((post, index) => (
                      <motion.div
                        key={post.post_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="p-5 bg-white border border-gray-100 shadow-md hover:shadow-lg transition-all"
                        style={{ borderRadius: '1.25rem' }}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center flex-shrink-0" style={{ borderRadius: '1rem' }}>
                            <Megaphone size={24} className="text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h3 className="text-lg font-bold text-gray-900">{post.title}</h3>
                              {/* Days left indicator - broadcasts max 15 days */}
                              {post.expires_at && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-medium whitespace-nowrap" style={{ borderRadius: '9999px' }}>
                                  <Clock size={12} />
                                  {Math.max(0, Math.ceil((new Date(post.expires_at) - new Date()) / (1000 * 60 * 60 * 24)))}d left
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600 text-sm mb-3">{post.description}</p>
                            
                            <div className="flex flex-wrap items-center gap-3 text-sm">
                              {post.destination && (
                                <span className="flex items-center gap-1 text-gray-500">
                                  <MapPin size={14} />
                                  {post.destination}
                                </span>
                              )}
                              {post.event_name && (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 font-medium" style={{ borderRadius: '9999px' }}>
                                  {post.event_name}
                                </span>
                              )}
                              {post.event_date && (
                                <span className="flex items-center gap-1 text-gray-500">
                                  <Calendar size={14} />
                                  {post.event_date}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                              <div className="flex items-center gap-4 text-sm text-gray-400">
                                <span className="flex items-center gap-1">
                                  <Eye size={14} />
                                  {post.view_count}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Heart size={14} />
                                  {post.interested_count}
                                </span>
                              </div>
                              <button
                                onClick={() => {
                                  if (!isAuthenticated) {
                                    toast.error('Please sign in first');
                                    navigate('/auth');
                                    return;
                                  }
                                  if (!isPro) {
                                    setShowProModal(true);
                                    return;
                                  }
                                  setSelectedPost(post);
                                }}
                                className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-1.5"
                                style={{ borderRadius: '9999px' }}
                              >
                                {!isPro && <Lock size={14} />}
                                I'm Interested
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* My Matches Tab */}
            {activeTab === 'matches' && (
              <div className="space-y-4">
                {!isAuthenticated ? (
                  <div className="text-center py-16">
                    <div className="w-24 h-24 bg-gray-100 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '2rem' }}>
                      <Lock size={48} className="text-gray-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Sign in to see matches</h3>
                    <p className="text-gray-500 mb-6">Your trip matches will appear here once you're signed in.</p>
                    <button 
                      onClick={() => navigate('/auth')}
                      className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold shadow-lg transition-all"
                      style={{ borderRadius: '9999px' }}
                    >
                      Sign In
                    </button>
                  </div>
                ) : myMatches.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-24 h-24 bg-rose-100 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '2rem' }}>
                      <HandHeart size={48} className="text-rose-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">No trip matches yet</h3>
                    <p className="text-gray-500 mb-6 max-w-md mx-auto">When you get accepted for a trip or accept someone's request, your matches will appear here.</p>
                    <button 
                      onClick={() => setActiveTab('trips')}
                      className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold shadow-lg transition-all"
                      style={{ borderRadius: '9999px' }}
                    >
                      Browse Trip Invites
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myMatches.map((match, index) => (
                      <motion.div
                        key={match.match_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-5 bg-white border border-gray-100 shadow-md"
                        style={{ borderRadius: '1.25rem' }}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-400 flex items-center justify-center" style={{ borderRadius: '1rem' }}>
                              <HandHeart size={24} className="text-white" />
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900">{match.destination}</h3>
                              <p className="text-sm text-gray-500">{match.time_window}</p>
                            </div>
                          </div>
                          
                          {match.chat_enabled ? (
                            <span className="flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-bold" style={{ borderRadius: '9999px' }}>
                              <MessageSquare size={14} />
                              Chat Active
                            </span>
                          ) : (
                            <button
                              onClick={() => setShowProModal(true)}
                              className="flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 text-sm font-bold hover:bg-amber-200 transition-colors"
                              style={{ borderRadius: '9999px' }}
                            >
                              <Crown size={14} />
                              Unlock Chat
                            </button>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">
                            {match.user_ids?.length || 2} travelers matched
                          </span>
                        </div>
                        
                        {match.chat_enabled && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <button
                              onClick={() => setActiveChatMatch(match)}
                              className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                              style={{ borderRadius: '9999px' }}
                              data-testid="open-trip-chat-btn"
                            >
                              <MessageSquare size={18} />
                              Open Trip Chat
                            </button>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
      
      {/* Create Post Modal */}
      <AnimatePresence>
        {showCreatePost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowCreatePost(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white shadow-2xl max-h-[90vh] overflow-y-auto"
              style={{ borderRadius: '1.5rem' }}
            >
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {createPostType === 'travel_invite' ? 'Create Travel Invite' : 'Create Broadcast'}
                </h3>
                
                {/* Post Type Toggle */}
                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => setCreatePostType('travel_invite')}
                    className={`flex-1 py-2.5 font-semibold transition-all ${
                      createPostType === 'travel_invite'
                        ? 'bg-teal-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                    style={{ borderRadius: '9999px' }}
                  >
                    <MapPin size={16} className="inline mr-1" />
                    Trip Invite
                  </button>
                  <button
                    onClick={() => setCreatePostType('broadcast')}
                    className={`flex-1 py-2.5 font-semibold transition-all ${
                      createPostType === 'broadcast'
                        ? 'bg-amber-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                    style={{ borderRadius: '9999px' }}
                  >
                    <Megaphone size={16} className="inline mr-1" />
                    Broadcast
                  </button>
                </div>
                
                <div className="space-y-4">
                  {createPostType === 'travel_invite' ? (
                    <>
                      <input
                        type="text"
                        value={newPost.destination}
                        onChange={(e) => setNewPost(p => ({ ...p, destination: e.target.value }))}
                        placeholder="Destination (e.g., Ladakh, Goa, Japan)"
                        className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 text-gray-900 placeholder-gray-400 focus:border-teal-400 outline-none"
                        style={{ borderRadius: '0.75rem' }}
                        data-testid="destination-input"
                      />
                      
                      <input
                        type="text"
                        value={newPost.time_window}
                        onChange={(e) => setNewPost(p => ({ ...p, time_window: e.target.value }))}
                        placeholder="Time window (e.g., June 2026, Summer 2026)"
                        className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 text-gray-900 placeholder-gray-400 focus:border-teal-400 outline-none"
                        style={{ borderRadius: '0.75rem' }}
                        data-testid="time-window-input"
                      />
                      
                      <div className="grid grid-cols-2 gap-3">
                        <select
                          value={newPost.travel_style}
                          onChange={(e) => setNewPost(p => ({ ...p, travel_style: e.target.value }))}
                          className="px-4 py-3 bg-gray-50 border-2 border-gray-100 text-gray-900 focus:border-teal-400 outline-none"
                          style={{ borderRadius: '0.75rem' }}
                        >
                          {TRAVEL_STYLES.map(style => (
                            <option key={style} value={style}>{style.charAt(0).toUpperCase() + style.slice(1)}</option>
                          ))}
                        </select>
                        
                        <select
                          value={newPost.group_size_preference}
                          onChange={(e) => setNewPost(p => ({ ...p, group_size_preference: e.target.value }))}
                          className="px-4 py-3 bg-gray-50 border-2 border-gray-100 text-gray-900 focus:border-teal-400 outline-none"
                          style={{ borderRadius: '0.75rem' }}
                        >
                          <option value="1">Solo (Looking for 1)</option>
                          <option value="2-3">Small (2-3 people)</option>
                          <option value="4-5">Medium (4-5 people)</option>
                          <option value="5+">Large (5+ people)</option>
                        </select>
                      </div>
                      
                      <select
                        value={newPost.budget_range}
                        onChange={(e) => setNewPost(p => ({ ...p, budget_range: e.target.value }))}
                        className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 text-gray-900 focus:border-teal-400 outline-none"
                        style={{ borderRadius: '0.75rem' }}
                      >
                        <option value="budget">Budget</option>
                        <option value="mid-range">Mid-Range</option>
                        <option value="luxury">Luxury</option>
                      </select>
                      
                      {/* Interests */}
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Interests (select up to 5)</p>
                        <div className="flex flex-wrap gap-2">
                          {INTEREST_OPTIONS.map(interest => (
                            <button
                              key={interest}
                              onClick={() => toggleInterest(interest)}
                              className={`px-3 py-1.5 text-sm font-medium transition-all ${
                                newPost.interests.includes(interest)
                                  ? 'bg-teal-500 text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              style={{ borderRadius: '9999px' }}
                            >
                              {interest}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={newPost.title}
                        onChange={(e) => setNewPost(p => ({ ...p, title: e.target.value }))}
                        placeholder="Title (e.g., Anyone attending Hemis Festival?)"
                        className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 text-gray-900 placeholder-gray-400 focus:border-teal-400 outline-none"
                        style={{ borderRadius: '0.75rem' }}
                      />
                      
                      <input
                        type="text"
                        value={newPost.destination}
                        onChange={(e) => setNewPost(p => ({ ...p, destination: e.target.value }))}
                        placeholder="Location (optional)"
                        className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 text-gray-900 placeholder-gray-400 focus:border-teal-400 outline-none"
                        style={{ borderRadius: '0.75rem' }}
                      />
                      
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={newPost.event_name}
                          onChange={(e) => setNewPost(p => ({ ...p, event_name: e.target.value }))}
                          placeholder="Event name (optional)"
                          className="px-4 py-3 bg-gray-50 border-2 border-gray-100 text-gray-900 placeholder-gray-400 focus:border-teal-400 outline-none"
                          style={{ borderRadius: '0.75rem' }}
                        />
                        
                        <input
                          type="text"
                          value={newPost.event_date}
                          onChange={(e) => setNewPost(p => ({ ...p, event_date: e.target.value }))}
                          placeholder="Event date (optional)"
                          className="px-4 py-3 bg-gray-50 border-2 border-gray-100 text-gray-900 placeholder-gray-400 focus:border-teal-400 outline-none"
                          style={{ borderRadius: '0.75rem' }}
                        />
                      </div>
                    </>
                  )}
                  
                  <textarea
                    value={newPost.description}
                    onChange={(e) => setNewPost(p => ({ ...p, description: e.target.value }))}
                    placeholder="Describe your trip or interest... What are you looking for?"
                    rows={4}
                    maxLength={createPostType === 'travel_invite' ? 280 : 500}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 text-gray-900 placeholder-gray-400 focus:border-teal-400 outline-none resize-none"
                    style={{ borderRadius: '0.75rem' }}
                    data-testid="description-input"
                  />
                  <p className="text-xs text-gray-400 text-right">
                    {newPost.description.length}/{createPostType === 'travel_invite' ? 280 : 500}
                  </p>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowCreatePost(false)}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 transition-all"
                    style={{ borderRadius: '9999px' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreatePost}
                    className={`flex-1 py-3 font-bold shadow-lg hover:shadow-xl transition-all text-white ${
                      createPostType === 'travel_invite'
                        ? 'bg-gradient-to-r from-teal-500 to-cyan-500'
                        : 'bg-gradient-to-r from-amber-500 to-orange-500'
                    }`}
                    style={{ borderRadius: '9999px' }}
                    data-testid="submit-post-btn"
                  >
                    Post
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Join Request Modal */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => { setSelectedPost(null); setSelectedTemplate(null); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white shadow-2xl p-6"
              style={{ borderRadius: '1.5rem' }}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Send Interest</h3>
              <p className="text-gray-500 mb-6">
                {selectedPost.post_type === 'travel_invite' 
                  ? `Express interest in the ${selectedPost.destination} trip`
                  : `Respond to: "${selectedPost.title}"`
                }
              </p>
              
              <div className="space-y-3 mb-6">
                {JOIN_TEMPLATES.map(template => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`w-full p-4 text-left border-2 transition-all flex items-center gap-3 ${
                      selectedTemplate === template.id
                        ? 'border-teal-400 bg-teal-50'
                        : 'border-gray-100 hover:border-gray-200'
                    }`}
                    style={{ borderRadius: '1rem' }}
                  >
                    <div className={`w-10 h-10 flex items-center justify-center ${
                      selectedTemplate === template.id ? 'bg-teal-100' : 'bg-gray-100'
                    }`} style={{ borderRadius: '0.75rem' }}>
                      <template.icon size={20} className={selectedTemplate === template.id ? 'text-teal-600' : 'text-gray-500'} />
                    </div>
                    <span className={selectedTemplate === template.id ? 'text-teal-700 font-medium' : 'text-gray-700'}>
                      {template.label}
                    </span>
                  </button>
                ))}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => { setSelectedPost(null); setSelectedTemplate(null); }}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 transition-all"
                  style={{ borderRadius: '9999px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSendJoinRequest(selectedPost.post_id)}
                  disabled={!selectedTemplate}
                  className={`flex-1 py-3 font-bold transition-all ${
                    selectedTemplate
                      ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                  style={{ borderRadius: '9999px' }}
                >
                  Send Request
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Pro Upgrade Modal */}
      <AnimatePresence>
        {showProModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowProModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white shadow-2xl overflow-hidden"
              style={{ borderRadius: '1.5rem' }}
            >
              {/* Pro Header */}
              <div className="p-6 bg-gradient-to-br from-amber-400 via-orange-400 to-rose-400 text-white text-center">
                <Crown size={48} className="mx-auto mb-3" />
                <h3 className="text-2xl font-black mb-2">Unlock Pro Features</h3>
                <p className="text-white/90">Communicate and plan trips together</p>
              </div>
              
              <div className="p-6">
                {/* Features */}
                <div className="space-y-3 mb-6">
                  {[
                    { icon: MessageSquare, text: 'Trip-specific chat with matched travelers' },
                    { icon: Phone, text: 'Optional contact exchange (with consent)' },
                    { icon: Zap, text: 'Priority in travel buddy matching' },
                    { icon: Shield, text: 'AI-moderated safe communication' },
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-amber-100 flex items-center justify-center" style={{ borderRadius: '0.5rem' }}>
                        <feature.icon size={16} className="text-amber-600" />
                      </div>
                      <span className="text-gray-700">{feature.text}</span>
                    </div>
                  ))}
                </div>
                
                {/* Pricing */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <button
                    onClick={() => navigate('/payment?plan=monthly')}
                    className="p-4 border-2 border-gray-200 hover:border-amber-400 transition-all text-center"
                    style={{ borderRadius: '1rem' }}
                  >
                    <p className="text-2xl font-black text-gray-900">₹299</p>
                    <p className="text-sm text-gray-500">/month</p>
                  </button>
                  <button
                    onClick={() => navigate('/payment?plan=annual')}
                    className="p-4 border-2 border-amber-400 bg-amber-50 transition-all text-center relative"
                    style={{ borderRadius: '1rem' }}
                  >
                    <span className="absolute -top-2 right-2 px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold" style={{ borderRadius: '9999px' }}>
                      Save 44%
                    </span>
                    <p className="text-2xl font-black text-gray-900">₹1,999</p>
                    <p className="text-sm text-gray-500">/year</p>
                  </button>
                </div>
                
                <button
                  onClick={() => setShowProModal(false)}
                  className="w-full py-2 text-gray-500 font-medium hover:text-gray-700 transition-colors"
                >
                  Maybe Later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Trip Chat Modal */}
      <TripChat
        match={activeChatMatch}
        userId={user?.user_id}
        isOpen={!!activeChatMatch}
        onClose={() => setActiveChatMatch(null)}
        isPro={isPro}
      />
      
      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 bg-white border-t border-gray-100">
        <div className="flex items-center justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Home size={22} />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link to="/saved" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Bookmark size={22} />
            <span className="text-xs font-medium">Saved</span>
          </Link>
          <Link 
            to="/planning"
            className="w-14 h-14 -mt-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 flex items-center justify-center"
            style={{ borderRadius: '50%' }}
          >
            <MapPin size={24} />
          </Link>
          <Link to="/community" className="flex flex-col items-center gap-1 p-2 text-teal-600">
            <Users size={22} />
            <span className="text-xs font-medium">Community</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <User size={22} />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </div>
      
      <FloatingSupport />
    </div>
  );
}
